import { useSelector } from 'react-redux';
import { selectPermissionsByResource } from '@features/auth/slice';
import {
  useIsSuperAdmin,
  useIsMerchant,
  useIsMerchantParent,
  useIsMultiMerchant,
  useIsVendor,
} from '@hooks/auth';
import { useFlagChecker } from '@hooks/feature-flags/useFeatureFlag';
import { appNavSections } from './appNav';
import {
  isNavItemVisible,
  type PermissionMap,
  type UserContext,
} from './navVisibility';

/**
 * True when the current user can see at least one top-level nav item — the
 * boolean form of "the sidebar is non-empty". A user with no role and no
 * permissions sees none, so the Home route renders blank instead of the
 * dashboard (TECH-22434). Applies the same per-item predicate the Sidebar
 * uses to decide visibility.
 */
export function useHasNavAccess(): boolean {
  const permissionIndex = useSelector(selectPermissionsByResource);
  const permissions: PermissionMap = {
    has: (resource, action) => permissionIndex[resource]?.has(action) ?? false,
  };

  const hasFlag = useFlagChecker();
  const ctx: UserContext = {
    superAdmin: useIsSuperAdmin(),
    merchant: useIsMerchant(),
    merchantParent: useIsMerchantParent(),
    multiMerchant: useIsMultiMerchant(),
    vendor: useIsVendor(),
  };

  return appNavSections.some((section) =>
    section.items.some((item) => isNavItemVisible(item, permissions, ctx, hasFlag)),
  );
}
